import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Moon, Sun, Sparkles } from "lucide-react";

const TIMEZONES = [
  { value: "UTC", label: "UTC — Temps universel" },
  { value: "Europe/Paris", label: "Europe/Paris (UTC+1/+2)" },
  { value: "Europe/London", label: "Europe/London (UTC+0/+1)" },
  { value: "Europe/Brussels", label: "Europe/Brussels (UTC+1/+2)" },
  { value: "Europe/Zurich", label: "Europe/Zurich (UTC+1/+2)" },
  { value: "Europe/Madrid", label: "Europe/Madrid (UTC+1/+2)" },
  { value: "Europe/Berlin", label: "Europe/Berlin (UTC+1/+2)" },
  { value: "Europe/Rome", label: "Europe/Rome (UTC+1/+2)" },
  { value: "Africa/Casablanca", label: "Africa/Casablanca (UTC+1)" },
  { value: "Africa/Algiers", label: "Africa/Algiers (UTC+1)" },
  { value: "Africa/Tunis", label: "Africa/Tunis (UTC+1)" },
  { value: "Africa/Cairo", label: "Africa/Cairo (UTC+2)" },
  { value: "Africa/Johannesburg", label: "Africa/Johannesburg (UTC+2)" },
  { value: "America/New_York", label: "America/New_York (UTC-5/-4)" },
  { value: "America/Toronto", label: "America/Toronto (UTC-5/-4)" },
  { value: "America/Chicago", label: "America/Chicago (UTC-6/-5)" },
  { value: "America/Denver", label: "America/Denver (UTC-7/-6)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (UTC-8/-7)" },
  { value: "America/Vancouver", label: "America/Vancouver (UTC-8/-7)" },
  { value: "America/Sao_Paulo", label: "America/Sao_Paulo (UTC-3)" },
  { value: "America/Mexico_City", label: "America/Mexico_City (UTC-6)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (UTC+9)" },
  { value: "Asia/Shanghai", label: "Asia/Shanghai (UTC+8)" },
  { value: "Asia/Hong_Kong", label: "Asia/Hong_Kong (UTC+8)" },
  { value: "Asia/Singapore", label: "Asia/Singapore (UTC+8)" },
  { value: "Asia/Seoul", label: "Asia/Seoul (UTC+9)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (UTC+4)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (UTC+5:30)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (UTC+10/+11)" },
  { value: "Australia/Melbourne", label: "Australia/Melbourne (UTC+10/+11)" },
  { value: "Pacific/Auckland", label: "Pacific/Auckland (UTC+12/+13)" },
];

export default function Settings() {
  const { theme, setTheme } = useTheme();
  const [s, setS] = useState(null);
  const [me, setMe] = useState(null);
  const [f, setF] = useState({ starting_capital: "", fee_percent: "", max_tracked_per_day: "", default_strategy: "sniper" });
  const [profile, setProfile] = useState({ nickname: "", timezone: "" });
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [capEnabled, setCapEnabled] = useState(
    () => localStorage.getItem("cap_disabled") !== "1"
  );

  const toggleCap = (v) => {
    setCapEnabled(v);
    localStorage.setItem("cap_disabled", v ? "0" : "1");
    window.dispatchEvent(new Event("cap-setting-changed"));
    toast.success(v ? "Assistant Cap activé" : "Assistant Cap masqué");
  };

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/auth/me")]).then(([sr, mr]) => {
      setS(sr.data);
      setF({
        starting_capital: sr.data.starting_capital,
        fee_percent: sr.data.fee_percent,
        max_tracked_per_day: sr.data.max_tracked_per_day ?? 10,
        default_strategy: sr.data.default_strategy || "sniper",
      });
      setMe(mr.data);
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setProfile({
        nickname: mr.data?.nickname || "",
        timezone: mr.data?.timezone || detected || "UTC",
      });
    });
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const r = await api.patch("/users/me", {
        nickname: profile.nickname.trim() || null,
        timezone: profile.timezone || null,
      });
      setMe(r.data);
      if (r.data?.timezone) localStorage.setItem("geni_timezone", r.data.timezone);
      else localStorage.removeItem("geni_timezone");
      toast.success("Profil sauvegardé");
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    } finally { setSavingProfile(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/settings", {
        starting_capital: Number(f.starting_capital),
        fee_percent: Number(f.fee_percent),
        max_tracked_per_day: Math.max(1, Math.floor(Number(f.max_tracked_per_day) || 10)),
        default_strategy: f.default_strategy || "sniper",
      });
      setS(r.data);
      toast.success("Réglages sauvegardés");
    } catch (e) {
      toast.error("Erreur", { description: e?.response?.data?.detail || String(e) });
    } finally { setSaving(false); }
  };

  if (!s) return <div className="text-muted-1">Chargement…</div>;

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-2 mb-1">Configuration</div>
        <h1 className="font-display text-3xl font-semibold">Réglages</h1>
      </div>

      {/* Theme */}
      <div className="bg-card-1 border border-line rounded-xl p-6 space-y-4">
        <h2 className="font-display text-lg">Apparence</h2>
        <Label className="text-[10px] uppercase tracking-widest text-muted-1">Thème</Label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setTheme("dark")}
            className={`flex items-center gap-3 p-4 rounded-lg border transition-colors text-left ${
              theme === "dark"
                ? "border-white bg-card-2"
                : "border-line hover:bg-card-2"
            }`}
            data-testid="theme-dark-btn"
          >
            <div className="w-10 h-10 rounded-md bg-[#0A0A0A] border border-[#333] flex items-center justify-center">
              <Moon size={16} style={{ color: "var(--text)" }} />
            </div>
            <div>
              <div className="text-sm font-medium">Sombre</div>
              <div className="text-[11px] text-muted-2">Par défaut · haut contraste, longues sessions</div>
            </div>
          </button>
          <button
            onClick={() => setTheme("light")}
            className={`flex items-center gap-3 p-4 rounded-lg border transition-colors text-left ${
              theme === "light"
                ? "border-black bg-card-2"
                : "border-line hover:bg-card-2"
            }`}
            data-testid="theme-light-btn"
          >
            <div className="w-10 h-10 rounded-md bg-[#F7F7F5] border border-[#D0D0CC] flex items-center justify-center">
              <Sun size={16} className="text-[#0A0A0A]" />
            </div>
            <div>
              <div className="text-sm font-medium">Clair</div>
              <div className="text-[11px] text-muted-2">Journée · lisibilité en plein soleil</div>
            </div>
          </button>
        </div>
      </div>

      {/* Assistant Cap on/off */}
      <div className="bg-card-1 border border-line rounded-xl p-6 space-y-4">
        <h2 className="font-display text-lg flex items-center gap-2">
          <Sparkles size={16} className="text-invest" />
          Assistant Cap
        </h2>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium">Afficher le bouton flottant</div>
            <div className="text-[11px] text-muted-2 mt-1">
              Le bouton rond de Cap apparaît en bas à droite. Décochez pour le masquer
              (vous conservez tout l'historique de conversation).
            </div>
          </div>
          <Switch
            checked={capEnabled}
            onCheckedChange={toggleCap}
            className="data-[state=unchecked]:bg-white"
            data-testid="cap-enabled-toggle"
          />
        </div>
      </div>

      {/* Profile: nickname + timezone */}
      <div className="bg-card-1 border border-line rounded-xl p-6 space-y-4">
        <h2 className="font-display text-lg">Profil</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">Surnom (raccourci de connexion)</Label>
            <Input
              value={profile.nickname}
              onChange={(e) => setProfile({ ...profile, nickname: e.target.value })}
              placeholder="ex : yannick"
              className="h-10 mt-1.5 bg-input border-line-strong font-mono-num"
              data-testid="settings-nickname"
            />
            <div className="text-[11px] text-muted-2 mt-1">
              3-32 caractères, sans espace ni @. Utilisé à la connexion à la place de l'email complet.
              L'OTP est toujours envoyé à votre adresse email : {me?.email}.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">Fuseau horaire</Label>
            <Select value={profile.timezone} onValueChange={(v) => setProfile({ ...profile, timezone: v })}>
              <SelectTrigger className="h-10 mt-1.5 bg-input border-line-strong font-mono-num" data-testid="settings-timezone">
                <SelectValue placeholder="Choisir un fuseau" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-muted-2 mt-1">
              Détecté auto : {Intl.DateTimeFormat().resolvedOptions().timeZone}. Modifie la journée courante affichée sur le Dashboard.
            </div>
          </div>
        </div>
        <Button
          onClick={saveProfile}
          disabled={savingProfile}
          className="btn-primary"
          data-testid="settings-save-profile-btn"
        >{savingProfile ? "Enregistrement…" : "Enregistrer le profil"}</Button>
      </div>

      {/* Capital & fees */}
      <div className="bg-card-1 border border-line rounded-xl p-6 space-y-4">
        <h2 className="font-display text-lg">Capital & discipline</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">Capital de départ ($)</Label>
            <Input
              value={f.starting_capital}
              onChange={(e) => setF({ ...f, starting_capital: e.target.value })}
              className="h-10 mt-1.5 bg-input border-line-strong font-mono-num"
              data-testid="settings-capital"
            />
            <div className="text-[11px] text-muted-2 mt-1">
              Le capital n'est jamais rechargé automatiquement après une perte. Les gains au-dessus sont mis en réserve.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">Frais estimés (%, aller-retour)</Label>
            <Input
              value={f.fee_percent}
              onChange={(e) => setF({ ...f, fee_percent: e.target.value })}
              className="h-10 mt-1.5 bg-input border-line-strong font-mono-num"
              data-testid="settings-fees"
            />
            <div className="text-[11px] text-muted-2 mt-1">
              Inclut commission et glissement estimé. Toujours déduit du P&L.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">Actions suivies par jour</Label>
            <Input
              type="number"
              min={1}
              value={f.max_tracked_per_day}
              onChange={(e) => setF({ ...f, max_tracked_per_day: e.target.value })}
              className="h-10 mt-1.5 bg-input border-line-strong font-mono-num"
              data-testid="settings-max-tracked"
            />
            <div className="text-[11px] text-muted-2 mt-1">
              Taille du top pré-market pris en compte. Une action tombant hors du top en fin de journée voit son rang de fin = 0.
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-1">Stratégie par défaut</Label>
            <Select
              value={f.default_strategy}
              onValueChange={(v) => setF({ ...f, default_strategy: v })}
            >
              <SelectTrigger className="h-10 mt-1.5 bg-input border-line-strong font-mono-num" data-testid="settings-default-strategy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sniper">Sniper — score pondéré</SelectItem>
                <SelectItem value="filet">Filet — équi-pondéré, filtres min.</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] text-muted-2 mt-1">
              Pré-remplie sur chaque nouvelle journée. Modifiable au moment d'ajouter la 1ère action.
            </div>
          </div>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="settings-save-btn"
        >{saving ? "Enregistrement…" : "Enregistrer"}</Button>
      </div>

      <div className="bg-card-1 border border-line rounded-xl p-6 text-xs text-muted-1 space-y-2">
        <div className="font-display text-sm uppercase tracking-widest text-app">Rappel de méthode</div>
        <p>• Aucune donnée n'est présentée comme une prédiction. Toutes les statistiques affichent un avertissement en cas d'échantillon insuffisant.</p>
        <p>• L'objectif des 50 à 100 premières journées est de savoir si le système a un edge réel — pas de gagner de l'argent.</p>
        <p>• Les extractions par image passent toujours par un écran d'aperçu avant enregistrement.</p>
      </div>
    </div>
  );
}
